import { useCallback, useEffect, useState } from "react";
import { X, RefreshCw, Activity } from "lucide-react";
import { get, post, fmtTime } from "@/lib/api";
import { DASHBOARD } from "@/constants/testIds";
import NudgeSection from "@/pages/NudgeSection";

function RunLog({ id }) {
  const [log, setLog] = useState(null);
  useEffect(() => { get(`/dashboard/runs/${id}`).then((r) => setLog(r.data.log || "")); }, [id]);
  return <pre className="runlog" data-testid={DASHBOARD.runLog}>{log === null ? "Loading…" : log}</pre>;
}

export default function StatusPanel({ onClose, onPublished }) {
  const [s, setS] = useState(null);
  const [runs, setRuns] = useState([]);
  const [open, setOpen] = useState(null);
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    const [a, b] = await Promise.all([get("/dashboard/status"), get("/dashboard/runs")]);
    if (a.ok) setS((prev) => { if (prev && prev.running && !a.data.running && a.data.last_result === "ok") onPublished(); return a.data; });
    if (b.ok) setRuns(b.data);
  }, [onPublished]);

  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, [load]);

  async function start() {
    setMsg("");
    const r = await post("/dashboard/refresh");
    setMsg(r.ok ? "Refresh started. It takes about 4 minutes; the page only changes if every gate passes." : r.data.error || "Could not start.");
    load();
  }

  return (
    <aside className="panel" data-testid={DASHBOARD.statusPanel} aria-label="Refresh status">
      <header>
        <b><Activity size={15} /> Refresh status</b>
        <button className="icon" onClick={onClose} aria-label="Close" data-testid={DASHBOARD.closePanel}><X size={16} /></button>
      </header>
      {!s ? <p className="muted">Loading…</p> : (
        <>
          <dl>
            <dt>Last result</dt>
            <dd data-testid={DASHBOARD.statusResult}>
              <span className={`pill ${s.running ? "run" : s.last_result === "ok" ? "ok" : s.last_result ? "bad" : ""}`}>
                {s.running ? "running" : s.last_result || "never run"}
              </span> {!s.running && s.last_message}
            </dd>
            <dt>Last success</dt><dd data-testid={DASHBOARD.statusLastSuccess}>{fmtTime(s.last_success)}</dd>
            <dt>Data as of</dt><dd>{s.published?.as_of || "—"}</dd>
            <dt>Next scheduled</dt><dd data-testid={DASHBOARD.statusNextRun}>{fmtTime(s.next_run)}</dd>
            <dt>Failures in a row</dt><dd>{s.consecutive_failures ?? 0}</dd>
          </dl>
          {s.warnings?.length > 0 && <ul className="warn" data-testid={DASHBOARD.statusWarnings}>{s.warnings.map((w) => <li key={w}>{w}</li>)}</ul>}
          <button className="primary sm" onClick={start} disabled={s.running} data-testid={DASHBOARD.refreshButton}>
            <RefreshCw size={14} className={s.running ? "spin" : ""} /> {s.running ? "Refreshing…" : "Refresh now"}
          </button>
          {msg && <p className="muted" data-testid={DASHBOARD.refreshMessage}>{msg}</p>}
        </>
      )}
      <NudgeSection />
      <h4>Recent runs</h4>
      <ul className="runs">
        {runs.map((r) => (
          <li key={r._id}>
            <button className="runrow" onClick={() => setOpen(open === r._id ? null : r._id)} data-testid={`${DASHBOARD.runRow}-${r._id}`}>
              <span className={`dot ${r.result}`} />{fmtTime(r.finished)}<small>{r.trigger}</small>
            </button>
            {open === r._id && <RunLog id={r._id} />}
          </li>
        ))}
        {runs.length === 0 && <li className="muted">No runs yet.</li>}
      </ul>
    </aside>
  );
}
